import debounce from 'lodash/debounce';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';

import useSlackCriteriaOptions from '../useSlackCriteriaOptions';
import { useFetchFlowDetailsQuery } from '../../../../../queries/Flows/Feed';
import {
  useDeleteConnectionIntegration,
  useSaveConnectionIntegration,
} from '../../../../../queries/Connections';
import { AutocompleteDropdownItem } from '../../../../../atomic/organism/Autocomplete/interfaces';

import {
  GetChannelsListResponse,
  SaveConnectionIntegrationRequest,
} from '../../../../../interfaces/Connections';
import { ConnectionState } from '../types';

const useConnectionsController = () => {
  const {
    connections,
    isLoadingChannels,
    selectedConnection,
    channelsListResponse,
    isLoadingConnections,
    setSelectedConnection,
  } = useSlackCriteriaOptions();

  const { flowId } = useParams<{ flowId: string }>();

  const { data: flowDetails } = useFetchFlowDetailsQuery(flowId, 'builder');

  const { mutate: saveConnection, isLoading: isSavingConnection } =
    useSaveConnectionIntegration(flowId);

  const {
    mutate: deleteConnection,
    isLoading: isDeletingConnection,
    isSuccess: isDisconnectedSuccess,
  } = useDeleteConnectionIntegration(flowId);

  const [applicationCacheId, setApplicationCacheId] = useState<string>('');

  const [state, setState] = useState(ConnectionState.ConnectState);

  const [connectionStatus, setConnectionStatus] = useState('');

  const [channelList, setChannelList] = useState<
    AutocompleteDropdownItem<string>[]
  >([]);

  const [connectionsList, setConnectionsList] = useState<
    AutocompleteDropdownItem<string>[]
  >([]);

  const [selectedPeopleToPostChannel, setSelectedPeopleToPostChannel] =
    useState<AutocompleteDropdownItem<string>[]>([]);

  const [selectedPostFlowResponseChannel, setSelectedPostFlowResponseChannel] =
    useState<AutocompleteDropdownItem<string>[]>([]);

  const updateConnection = useCallback(
    (
      _applicationCacheId: string,
      _selectedConnection: AutocompleteDropdownItem<string>[],
      _selectedPeopleToPostChannel: AutocompleteDropdownItem<string>[],
      _selectedPostFlowResponseChannel: AutocompleteDropdownItem<string>[],
      _saveConnection: typeof saveConnection,
    ) => {
      if (_applicationCacheId) {
        const payload: SaveConnectionIntegrationRequest = {
          integrations: [],
          connectionId:
            _selectedConnection && _selectedConnection.length
              ? _selectedConnection[0].id
              : '',
          applicationCacheId: _applicationCacheId,
        };

        if (_selectedPeopleToPostChannel.length) {
          payload.integrations.push({
            type: 'participationNotification',
            source: {
              type: 'channels',
              id: _selectedPeopleToPostChannel.map((item) => item.id),
            },
          });
        }

        if (_selectedPostFlowResponseChannel.length) {
          payload.integrations.push({
            type: 'responseNotification',
            source: {
              type: 'channels',
              id: _selectedPostFlowResponseChannel.map((item) => item.id),
            },
          });
        }

        _saveConnection(payload);
      }
    },
    [],
  );

  useEffect(() => {
    if (isDisconnectedSuccess) {
      setState(ConnectionState.ConnectState);
    }
  }, [isDisconnectedSuccess]);

  useEffect(() => {
    if (!isLoadingChannels) {
      const response = channelsListResponse as GetChannelsListResponse;

      if (applicationCacheId !== response.applicationCacheId) {
        setApplicationCacheId(response.applicationCacheId);
      }
      setChannelList(
        response.channels.map((channel) => ({
          id: channel.id,
          title: channel.displayName,
          icon: channel.isPrivate ? 'lock' : 'hashtag-lite',
        })),
      );
    }
  }, [applicationCacheId, channelsListResponse, isLoadingChannels]);

  useEffect(() => {
    if (connections && !isLoadingConnections) {
      const dropdownList: AutocompleteDropdownItem<string>[] = [
        ...connections.data.map((connection) => ({
          id: connection.connectionId,
          title: connection.workspaceName,
          avatar: {
            img: connection.workspaceLogo,
            userId: connection.connectionId,
          },
        })),
      ];

      setConnectionsList(dropdownList);

      if (flowDetails && flowDetails.data && flowDetails.data.integrations) {
        let selectedConnectionId = '';
        const { participationNotification, responseNotification } =
          flowDetails.data.integrations;

        if (participationNotification.length > 0) {
          selectedConnectionId = participationNotification[0].connectionId;
        }

        if (responseNotification.length > 0) {
          selectedConnectionId = responseNotification[0].connectionId;
        }

        if (
          state === ConnectionState.ConnectionEditState &&
          !selectedConnectionId
        ) {
          setSelectedConnection([dropdownList[0]]);
        } else {
          const itemToSelect = dropdownList.filter(
            (item) => item.id === selectedConnectionId,
          );
          if (itemToSelect.length > 0) {
            setSelectedConnection(itemToSelect);
          }
        }
      }
    }
  }, [connections, isLoadingConnections]);

  useEffect(() => {
    if (
      connections &&
      !isLoadingConnections &&
      connections.data.length > 0 &&
      state === ConnectionState.ConnectToSlackState
    ) {
      setSelectedConnection([connectionsList[0]]);
      setState(ConnectionState.ConnectionEditState);
    }
  }, [
    state,
    connections,
    connectionsList,
    isLoadingConnections,
    setSelectedConnection,
  ]);

  useEffect(() => {
    if (!selectedConnection || !selectedConnection.length) {
      setSelectedPeopleToPostChannel([]);
      setSelectedPostFlowResponseChannel([]);
    }
  }, [selectedConnection]);

  // Saved Button
  const [showSavedButton, setShowSavedButton] = useState(false);

  useEffect(() => {
    if (!isSavingConnection) {
      setShowSavedButton(true);
      const timeoutId = setTimeout(() => {
        setShowSavedButton(false);
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isSavingConnection]);

  // Flow Details
  useEffect(() => {
    if (flowDetails && flowDetails.data) {
      let status = '';
      if (flowDetails.data.integrations) {
        const { participationNotification, responseNotification } =
          flowDetails.data.integrations;

        if (participationNotification && participationNotification.length > 0) {
          status = participationNotification[0].connectionStatus;
        }

        if (responseNotification && responseNotification.length > 0) {
          status = responseNotification[0].connectionStatus;
        }
      }

      if (
        status === 'CONNECTED' &&
        state !== ConnectionState.ConnectionEditState
      ) {
        setState(ConnectionState.ConnectionDisplayState);
      }
      setConnectionStatus(status);
    }
  }, [flowDetails, state]);

  useEffect(() => {
    if (flowDetails && channelList.length > 0) {
      if (flowDetails.data.integrations) {
        const {
          data: {
            integrations: { participationNotification, responseNotification },
          },
        } = flowDetails;

        if (
          participationNotification.length > 0 &&
          participationNotification[0].metaData &&
          participationNotification[0].metaData.channelInfo.length > 0
        ) {
          const channelsIdToBeSelected =
            participationNotification[0].metaData.channelInfo.map(
              (channel) => channel.id,
            );

          setSelectedPeopleToPostChannel(
            channelList.filter((channel) =>
              channelsIdToBeSelected.includes(channel.id),
            ),
          );
        }

        if (
          responseNotification.length > 0 &&
          responseNotification[0].metaData &&
          responseNotification[0].metaData.channelInfo.length > 0
        ) {
          const channelsIdToBeSelected =
            responseNotification[0].metaData.channelInfo.map(
              (channel) => channel.id,
            );

          setSelectedPostFlowResponseChannel(
            channelList.filter((channel) =>
              channelsIdToBeSelected.includes(channel.id),
            ),
          );
        }
      }
    }
  }, [channelList]);

  const debounceSaveConnection = useRef(
    debounce(
      (
        _applicationCacheId: string,
        _selectedConnection: AutocompleteDropdownItem<string>[],
        _selectedPeopleToPostChannel: AutocompleteDropdownItem<string>[],
        _selectedPostFlowResponseChannel: AutocompleteDropdownItem<string>[],
        _saveConnection: typeof saveConnection,
      ) => {
        setShowSavedButton(false);
        updateConnection(
          _applicationCacheId,
          _selectedConnection,
          _selectedPeopleToPostChannel,
          _selectedPostFlowResponseChannel,
          _saveConnection,
        );
      },
      500,
    ),
  );

  useEffect(() => {
    debounceSaveConnection.current(
      applicationCacheId,
      selectedConnection,
      selectedPeopleToPostChannel,
      selectedPostFlowResponseChannel,
      saveConnection,
    );
  }, [
    applicationCacheId,
    selectedConnection,
    selectedPeopleToPostChannel,
    selectedPostFlowResponseChannel,
  ]);

  return {
    state,
    setState,
    channelList,
    flowDetails,
    showSavedButton,
    connectionsList,
    connectionStatus,
    deleteConnection,
    isLoadingChannels,
    selectedConnection,
    isSavingConnection,
    isDeletingConnection,
    isLoadingConnections,
    setSelectedConnection,
    selectedPeopleToPostChannel,
    setSelectedPeopleToPostChannel,
    selectedPostFlowResponseChannel,
    setSelectedPostFlowResponseChannel,
  };
};

export default useConnectionsController;
